import React, { Component } from "react";
import { connect } from "react-redux";
import { func, string, bool, object, instanceOf } from "prop-types";
import PageContent from "../PageContent";
import {
  challengeTemplateUpload,
  challengeCreate,
} from "../../logic/actions/api/liveness";
import { arrayBufferToBase64 } from "../../logic/arrayBufferToBase64";

import {
  instantiateFaceLiveness3D,
  onUpdate,
  clear3dflState,
} from "../../logic/actions/faceLiveness3D";
import { go } from "../../logic/actions/navigation";
import FaceLiveness3DUI from "../FaceLiveness3DUI";
import {
  logEvent,
  LIVENESS_TEST_STARTED,
  STEP_COMPLETED,
  FL3D_ERROR,
} from "../../logic/eventLogger";
import { SVR3DFL } from "../../logic/enums/livenessTestType";
import {
  THANK_YOU,
  LIVENESS_TEST_RETRY,
  INSTRUCTION_VIDEO_FACE_LIVENESS_3D,
} from "../../logic/enums/pages";
import {
  faceLivenessMovementDuration,
  faceLivenessMovementDelay,
} from "../../logic/faceLivenessTimingConfiguration";
import CaptureButtonPanel from "../CaptureButtonPanel";
import { CONNECTION_ISSUES } from "../../logic/enums/errorMessages";

const width = 480;
const height = 640;

export class PageFaceLiveness3D extends Component {
  static propTypes = {
    instantiateFaceLiveness3D: func,
    onUpdate: func.isRequired,
    go: func.isRequired,
    instance: instanceOf(Object),
    message: string,
    error: string,
    isReady: bool,
    faceBox: object,
  };

  constructor(props) {
    super(props);
    this.state = {
      isRunning: false,
      isMetadataLoaded: false,
      cameraPermission: true,
      toggleLoading: true,
    };

    this.onCameraStarted.bind(this);
    this.start.bind(this);

    document.addEventListener("visibilitychange", this.listenVisibilityChange);
  }

  componentWillMount() {
    if (!this.props.instance) {
      this.props.instantiateFaceLiveness3D();
    }
  }

  onCameraStarted = (fc) => {
    this.setState({ fc });
    this.video = fc.camera.videoEl;
    this.video.addEventListener("loadedmetadata", this.loadVideoMetadata);
  };

  loadVideoMetadata = () => {
    this.setState({ isMetadataLoaded: true });
    this.initFL3D(this.video);
  };

  start = () => {
    logEvent(LIVENESS_TEST_STARTED, { value: SVR3DFL });
    logEvent(LIVENESS_TEST_STARTED + "_" + SVR3DFL.toLowerCase());
    this.setState({ isRunning: true });
    this.props.instance.startSession();
  };

  initFL3D(video) {
    this.props.instance.setOptionalConfig({
      allowMP4: false,
    });

    this.props.instance.initialize({
      video,
      onUpdate: this.props.onUpdate,
      onTemplateCreated: this.onTemplateCreated,
      movementDelay: faceLivenessMovementDelay,
      movementDuration: faceLivenessMovementDuration,
    });
    this.props.instance.startProcessing();
  }

  onTemplateCreated = (tpl) => {
    this.setState({ pending: true });
    const base64template = arrayBufferToBase64(tpl);
    this.props
      .challengeTemplateUpload(base64template)
      .then((response) => {
        if (response.processingStatus === "PROCESSED") {
          logEvent(STEP_COMPLETED, { value: "liveness_test" });
          logEvent(STEP_COMPLETED + "_liveness_test");
          this.props.instance.terminate();
          this.props.go(THANK_YOU);
        } else {
          this.setState({ pending: false });
          logEvent(FL3D_ERROR, {
            value: response.processingErrors.items[0].message,
          });
          logEvent(
            FL3D_ERROR + `_${response.processingErrors.items[0].message}`
          );
          this.props.go(LIVENESS_TEST_RETRY);
        }
        return response;
      })
      .catch((err) => {
        console.error(err);
        if (this.videoRef && this.videoRef.current) {
          this.videoRef.current.srcObject = undefined;
        }
        this.state.fc.stopCamera();
        this.onError();
      });
  };

  cameraPermissionDenied = () => {
    this.setState({
      cameraPermission: false,
      message:
        "Please go to browser settings, enable camera permission and then click on button to confirm.",
      isError: true,
      retryCameraInit: false,
    });
  };

  retryCameraInit = () => {
    if (!this.state.cameraPermission)
      this.setState({
        cameraPermission: true,
        retryCameraInit: true,
        message: "",
        isError: false,
      });
  };

  onError = () => {
    this.setState({
      pending: false,
      networkError: true,
      message: CONNECTION_ISSUES,
    });

    setTimeout(() => {
      this.props.go(LIVENESS_TEST_RETRY);
    }, 3000);
  };

  setSpinnerState(spinnerState) {
    this.setState({ toggleLoading: spinnerState });
  }

  componentWillUnmount() {
    this.props.clear3dflState();
    document.removeEventListener(
      "visibilitychange",
      this.listenVisibilityChange
    );
    this.video.removeEventListener("loadedmetadata", this.loadVideoMetadata);
    this.video = null;
  }

  listenVisibilityChange = () => {
    this.props.go(INSTRUCTION_VIDEO_FACE_LIVENESS_3D);
  };

  redOverlayLoaded = () => {
    this.setState({ isRedOverlayLoaded: true });
  };

  greenOverlayLoaded = () => {
    this.setState({ isGreenOverlayLoaded: true });
  };

  render() {
    const messageClass = `message-box ${
      this.state.networkError ? "message-box-error" : ""
    }`;
    const btnEnabled =
      !this.state.isRunning &&
      this.props.isReady &&
      this.state.isGreenOverlayLoaded &&
      this.state.isRedOverlayLoaded;
    return (
      <PageContent
        toggleLoading={
          !this.props.isReady ||
          this.state.pending ||
          !this.state.isRedOverlayLoaded
        }
        type="capture"
      >
        <h1 className="capture-header">FACE LIVENESS</h1>
        <div>
          <div
            className="curtain"
            style={{
              display: this.state.isRunning ? "block" : "none",
              backgroundColor: this.state.colorCode,
            }}
          />
          {this.props.instance && (
            <FaceLiveness3DUI
              onCameraStarted={this.onCameraStarted}
              width={width}
              height={height}
              isRunning={this.state.isRunning}
              isReady={this.props.isReady}
              result={this.state.result}
              score={this.state.score}
              retryCameraInit={this.state.retryCameraInit}
              cameraPermissionDenied={this.cameraPermissionDenied}
              redOverlayLoaded={this.redOverlayLoaded}
              greenOverlayLoaded={this.greenOverlayLoaded}
              onError={this.onError}
            />
          )}
        </div>
        <footer>
          <div className={messageClass}>{this.state.message}</div>
          {!this.state.cameraPermission && (
            <button className={"btn blue"} onClick={this.retryCameraInit}>
              CONFIRM CAMERA PERMISSION
            </button>
          )}
          {this.state.cameraPermission && !this.state.isRunning && (
            <CaptureButtonPanel
              onCaptureClick={this.start}
              disabled={!btnEnabled}
              backDisabled={!btnEnabled}
              text="Start"
            />
          )}
        </footer>
      </PageContent>
    );
  }
}

export default connect(
  (state) => {
    return { ...state.faceLiveness3D };
  },
  {
    instantiateFaceLiveness3D,
    onUpdate,
    challengeTemplateUpload,
    challengeCreate,
    go,
    clear3dflState,
  }
)(PageFaceLiveness3D);
